import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';

export default function ResumenGlobalSesiones({
    sacGeneradas = [],
    sacNoGeneradas = [],
    saeGeneradas = [],
    saeNoGeneradas = [],
    areasTematicas = [],
    ambitoTitulo = 'NIVEL GLOBAL',
}) {
    const resumen = useMemo(() => {
        const inicial = {
            primaria: 0,
            secundaria: 0,
        };

        const normalizarNivel = (valor = '') => String(valor).trim().toLowerCase();

        const obtenerCantidad = (item) => {
            return Number(item?.total ?? item?.cantidad ?? item?.conteo ?? 0);
        };

        const agruparPorNivel = (data = []) => {
            return (Array.isArray(data) ? data : []).reduce(
                (acc, item) => {
                    const nivel = normalizarNivel(
                        item?.nivel || item?.nivel_nombre || item?.nombre_nivel
                    );
                    const cantidad = obtenerCantidad(item);

                    if (nivel.includes('primaria')) {
                        acc.primaria += cantidad;
                    } else if (nivel.includes('secundaria')) {
                        acc.secundaria += cantidad;
                    }

                    return acc;
                },
                { ...inicial }
            );
        };

        const sacGen = agruparPorNivel(sacGeneradas);
        const sacProc = agruparPorNivel(sacNoGeneradas);
        const saeGen = agruparPorNivel(saeGeneradas);
        const saeProc = agruparPorNivel(saeNoGeneradas);

        const totalGeneral =
            sacGen.primaria +
            sacProc.primaria +
            sacGen.secundaria +
            sacProc.secundaria +
            saeGen.primaria +
            saeProc.primaria +
            saeGen.secundaria +
            saeProc.secundaria;

        return {
            sac: {
                primaria: {
                    generadas: sacGen.primaria,
                    enProceso: sacProc.primaria,
                    total: sacGen.primaria + sacProc.primaria,
                },
                secundaria: {
                    generadas: sacGen.secundaria,
                    enProceso: sacProc.secundaria,
                    total: sacGen.secundaria + sacProc.secundaria,
                },
            },
            sae: {
                primaria: {
                    generadas: saeGen.primaria,
                    enProceso: saeProc.primaria,
                    total: saeGen.primaria + saeProc.primaria,
                },
                secundaria: {
                    generadas: saeGen.secundaria,
                    enProceso: saeProc.secundaria,
                    total: saeGen.secundaria + saeProc.secundaria,
                },
            },
            totalGeneral,
        };
    }, [sacGeneradas, sacNoGeneradas, saeGeneradas, saeNoGeneradas]);

    const textoAreas = useMemo(() => {
        if (!Array.isArray(areasTematicas) || !areasTematicas.length) return '';

        const formatearNombreArea = (texto = '') => {
            const limpio = String(texto).trim().toLowerCase();
            return limpio ? limpio.charAt(0).toUpperCase() + limpio.slice(1) : '';
        };

        return areasTematicas
            .map((area) => {
                if (typeof area === 'string') {
                    return formatearNombreArea(area);
                }

                const nombres = Array.isArray(area?.nombres) && area.nombres.length
                    ? area.nombres
                    : [area?.nombre || area?.descripcion || area?.sigla];

                const nombresFormateados = [...new Set(nombres)]
                    .map(formatearNombreArea)
                    .join(' / ');

                return `${nombresFormateados} (${area.sigla})`;
            })
            .join(', ');
    }, [areasTematicas]);
    
    return (
        <Box sx={{ mb: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 900,
                    color: '#01579b',
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                }}
            >
                RESUMEN DE SESIONES DE APRENDIZAJE, {ambitoTitulo}
            </Typography>

            {!!textoAreas && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Áreas temáticas: {textoAreas}
                </Typography>
            )}

            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#9f9f9f' }}>
                        <TableRow>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                            >
                                TIPO
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                            >
                                NIVEL
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                            >
                                GENERADAS
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                            >
                                EN PROCESO
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                TOTAL
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        <TableRow hover>
                            <TableCell
                                rowSpan={2}
                                align="center"
                                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                            >
                                SAC
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                Primaria
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                {resumen.sac.primaria.generadas}
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                {resumen.sac.primaria.enProceso}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                {resumen.sac.primaria.total}
                            </TableCell>
                        </TableRow>

                        <TableRow hover>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                Secundaria
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                {resumen.sac.secundaria.generadas}
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                {resumen.sac.secundaria.enProceso}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                {resumen.sac.secundaria.total}
                            </TableCell>
                        </TableRow>

                        <TableRow hover>
                            <TableCell
                                rowSpan={2}
                                align="center"
                                sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                            >
                                SAE
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                Primaria
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                {resumen.sae.primaria.generadas}
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                {resumen.sae.primaria.enProceso}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                {resumen.sae.primaria.total}
                            </TableCell>
                        </TableRow>

                        <TableRow hover>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                Secundaria
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                {resumen.sae.secundaria.generadas}
                            </TableCell>
                            <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0' }}>
                                {resumen.sae.secundaria.enProceso}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                {resumen.sae.secundaria.total}
                            </TableCell>
                        </TableRow>

                        <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                            <TableCell
                                colSpan={4}
                                align="center"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1.05rem',
                                    color: '#01579b',
                                    borderRight: '1px solid #e0e0e0',
                                }}
                            >
                                TOTAL GENERAL
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1.1rem',
                                    bgcolor: '#bbdefb',
                                    color: '#01579b',
                                }}
                            >
                                {resumen.totalGeneral}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
